//-- Copyright (c) 2021 TMT Insights Group, LLC
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { darkBaseTheme, globalBaseTheme, lightBaseTheme, colors as globalColors } from '@/constants/baseThemes';
import logo from '../assets/pbs_logo.svg';

// https://mui.com/customization/theming/

export const colors = {
  light: deepmerge(globalColors.light, {
    primary: {
      main: '#4c5fff',
      dark: '#0F1E8C',
      light: '#4553C4'
    },
    secondary: {
      main: '#2EBAB3',
      light: '#6FC1BD',
      dark: '#267873'
    },
    tertiary: {
      main: '#FFD524',
      light: '#FFE470 ',
      dark: '#CCAA1D'
    },
    info: {
      main: '#21A0F4',
      dark: '#1C86CC',
      light: '#49B0F4'
    },
    success: {
      main: '#1FCD1C',
      dark: '#189915',
      light: '#5CCD5A'
    },
    warning: {
      main: '#FFA100',
      light: '#FFBD4D',
      dark: '#E59100'
    },
    pending: {
      main: '#FF7D03',
      dark: '#E57103',
      light: '#FF9736'
    },
    error: {
      main: '#EC2247',
      light: '#EC506C',
      dark: '#D41E40'
    },
    teal: {
      main: '#2ABDBB',
      dark: '#24A3A1',
      light: '#88BDBC'
    },
    lavender: {
      main: '#C669FF',
      dark: '#9E54CC',
      light: '#DA9CFF'
    },
    background: {
      default: '#eff2f5',
      paper: '#ffffff'
    },
    action: {
      hover: 'rgba(0,0,0,.1)',
      focus: 'rgba(0,0,0,.04)'
    }
  }),
  dark: deepmerge(globalColors.dark, {
    primary: {
      main: '#5c6df2',
      dark: '#2638C4',
      light: '#878EC4'
    },
    secondary: {
      main: '#2caba4',
      light: '#9BD4D1',
      dark: '#35A19B'
    },
    tertiary: {
      main: '#FFD524',
      light: '#FFE470 ',
      dark: '#CCAA1D'
    },
    info: {
      main: '#21A0F4',
      dark: '#1C86CC',
      light: '#49B0F4'
    },
    success: {
      main: '#1FCD1C',
      dark: '#189915',
      light: '#5CCD5A'
    },
    warning: {
      main: '#FFA100',
      light: '#FFBD4D',
      dark: '#E59100'
    },
    pending: {
      main: '#FF7D03',
      dark: '#E57103',
      light: '#FF9736'
    },
    error: {
      main: '#EC2247',
      light: '#EC506C',
      dark: '#D41E40'
    },
    teal: {
      main: '#2ABDBB',
      dark: '#24A3A1',
      light: '#88BDBC'
    },
    lavender: {
      main: '#C669FF',
      dark: '#9E54CC',
      light: '#DA9CFF'
    },
    background: {
      default: '#111317',
      paper: '#1b1d27'
    },
    action: {
      hover: 'rgba(0,0,0,.1)',
      focus: 'rgba(0,0,0,.04)'
    }
  })
};

const globalTheme = deepmerge(globalBaseTheme, {
  shape: { borderRadius: 5 },
  customOverrides: {
    Header: {
      background: 'linear-gradient(45deg, #0b0c0e, #000)',
      '& h1 > img': {
        height: 50,
        width: 134
      }
    },
    Footer: {
      background: 'none'
    },
    TableStatus: {
      fontWeight: 800
    },
    TitleSearchTitleType: {
      '& .MuiChip-root': {
        width: 'auto !important'
      }
    },
    VersionCard: {
      backgroundColor: '#2451883f',
      '& > button': {
        fontSize: '1rem'
      }
    },
    StatusCard: {
      '& $icon': {
        borderRadius: 8,
        padding: 10,
        top: 10,
        right: 10,
        '& > svg': {
          fontSize: '1.4rem'
        }
      },
      label: {
        fontSize: '1.1rem'
      }
    },
    JobReviewHeading: {
      '& .MuiTypography-caption': {
        color: 'white',
        opacity: 0.8
      }
    },
    JobTitleCard: {
      '& .MuiChip-outlinedPrimary': {
        display: 'none'
      }
    },
    TypeLabel: {
      root: {
        fontSize: '.8rem'
      },
      series: {
        backgroundColor: '#ff8600 !important'
      },
      episode: {
        backgroundColor: '#00c7bc !important'
      },
      feature: {
        backgroundColor: '#2e8ee9 !important'
      },
      trailer: {
        backgroundColor: '#8855ee !important'
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 5
        }
      }
    }
  },
  components: {}
});

const light = {
  palette: {
    mode: 'light',
    ...colors.light
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    DashboardWidget: {
      background: 'linear-gradient(212deg, #FFF 6.88%, rgba(255, 255, 255, 0.46) 83.4%)'
    },
    TitleBar: {
      background: '#ffffffc2'
    },
    Footer: {
      background: '#ffffffc2'
    },
    TypeLabel: {
      root: {
        boxShadow: 'none !important'
      }
    },
    ExpandRow: {
      backgroundColor: '#fff',
      '&.MuiTableRow-root:hover': {
        backgroundColor: '#fff'
      }
    },
    InventoryFile: {
      border: `1px solid #d7d7d7`,
      backgroundColor: '#faf9f9'
    },
    TitleArea: {
      border: `1px solid #d7d7d7`,
      boxShadow: '0px 10px 13px -6px rgb(0 0 0 / 4%), 0px 20px 31px 3px rgb(0 0 0 / 3%), 0px 8px 38px 7px rgb(0 0 0 / 2%)'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.light.primary.main,
          '--primary-light': colors.light.primary.light,
          '--primary-dark': colors.light.primary.dark,
          '--secondary-main': colors.light.secondary.main,
          '--secondary-light': colors.light.secondary.light,
          '--secondary-dark': colors.light.secondary.dark,
          '--tertiary-main': colors.light.tertiary.main,
          '--tertiary-light': colors.light.tertiary.light,
          '--tertiary-dark': colors.light.tertiary.dark,
          '--default-main': colors.light.default.main,
          '--default-light': colors.light.default.light,
          '--default-dark': colors.light.default.dark,
          '--warning-main': colors.light.warning.main,
          '--warning-light': colors.light.warning.light,
          '--warning-dark': colors.light.warning.dark,
          '--success-main': colors.light.success.main,
          '--success-light': colors.light.success.light,
          '--success-dark': colors.light.success.dark,
          '--teal-main': colors.light.teal.main,
          '--teal-light': colors.light.teal.light,
          '--teal-dark': colors.light.teal.dark
        }
      }
    }
  }
};

const dark = {
  palette: {
    mode: 'dark',
    ...colors.dark
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    AddedJobAccordion: {
      backgroundColor: '#263141'
    },
    ButtonBar: {
      backgroundColor: 'rgba(9, 45, 90, 0.96)'
    },
    DashboardWidget: {
      background: 'linear-gradient(212deg, #353C50 9.41%, rgba(40, 45, 59, 0.52) 38.94%)'
    },
    TitleBar: {
      background: '#1A1D25',
      borderTop: '1px solid #111317'
    },
    InventoryFile: {
      backgroundColor: '#040404'
    },
    TitleSearch: {
      '& .MuiInputBase-root': {
        background: '#0909094a'
      }
    },
    TypeLabel: {
      root: {
        color: '#1E1E1E'
      }
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--primary-main': colors.dark.primary.main,
          '--primary-light': colors.dark.primary.light,
          '--primary-dark': colors.dark.primary.dark,
          '--secondary-main': colors.dark.secondary.main,
          '--secondary-light': colors.dark.secondary.light,
          '--secondary-dark': colors.dark.secondary.dark,
          '--tertiary-main': colors.dark.tertiary.main,
          '--tertiary-light': colors.dark.tertiary.light,
          '--tertiary-dark': colors.dark.tertiary.dark,
          '--default-main': colors.dark.default.main,
          '--default-light': colors.dark.default.light,
          '--default-dark': colors.dark.default.dark,
          '--warning-main': colors.dark.warning.main,
          '--warning-light': colors.dark.warning.light,
          '--warning-dark': colors.dark.warning.dark,
          '--success-main': colors.dark.success.main,
          '--success-light': colors.dark.success.light,
          '--success-dark': colors.dark.success.dark,
          '--teal-main': colors.dark.teal.main,
          '--teal-light': colors.dark.teal.light,
          '--teal-dark': colors.dark.teal.dark
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        containedSecondary: {
          color: '#000000'
        }
      }
    }
  }
};

const lightMergedWithGlobal = deepmerge(light, globalTheme);
const darkMergedWithGlobal = deepmerge(dark, globalTheme);

const lightTheme = createTheme(deepmerge(lightBaseTheme, lightMergedWithGlobal));
const darkTheme = createTheme(deepmerge(darkBaseTheme, darkMergedWithGlobal));

export default { darkTheme, lightTheme, logo };
